import { CompRef, DocumentServicesObject } from '@wix/document-services-types';
import { EditorElement } from '@wix/component-protocol/schema';
import { VIRTUAL_OOI_SLOT_SEPARATOR, SLOT_ID_DIVIDER } from './constants';
import {
  IPublicManifestAPI,
  UniversalSlotPointer,
} from '@wix/editor-platform-host-integration-apis';
import { IExperimentsApiAdapter } from '../../utils/experimentsApiAdapter';
import { getSlotDefinitions } from './utils/slotDefinitions';
import { ManifestSlotPathFinder } from './utils/manifestSlotPathFinder';

const NOT_VALID_SLOT_ERROR = 'Slot data is not valid';

interface OOISlotPointer {
  slotRef: CompRef;
  slotId?: string;
}

interface BlocksSlotPointer {
  slotRef: CompRef;
  slotId?: string;
}

export interface BuilderSlotPointer {
  hostRef: CompRef;
  slotId: string;
  slotKeyName: string;
  innerElementPath?: string[];
}

type SlotPointerInputParams =
  | OOISlotPointer
  | BlocksSlotPointer
  | BuilderSlotPointer;

/**
 * SlotPointer serves for:
 * - identifying slot in the OOI / Blocks / Builder / any future type host widget
 * - gracefully migrate to dev-center slotId in needed methods
 */
export class SlotPointer {
  slotRef?: CompRef;
  slotId?: string;
  hostRef?: CompRef;
  slotKeyName?: string;
  innerElementPath?: string[];
  type: 'OOI' | 'Blocks' | 'Builder';

  constructor(params: SlotPointerInputParams) {
    if (params.slotId) {
      this.slotId = params.slotId;
    }

    if ('slotRef' in params) {
      this.slotRef = params.slotRef;
    }

    if ('hostRef' in params) {
      this.hostRef = params.hostRef;
      this.slotKeyName = params.slotKeyName;
      this.innerElementPath = params.innerElementPath;
    }
    this.type = this.#extractSlotType();
  }

  #extractSlotType(): 'Blocks' | 'OOI' | 'Builder' {
    if (typeof this.slotRef === 'undefined') {
      return 'Builder';
    }
    if (this.slotRef && this.slotRef.id.includes(VIRTUAL_OOI_SLOT_SEPARATOR)) {
      return 'OOI';
    }

    if (this.slotRef && this.slotRef.id.includes(SLOT_ID_DIVIDER)) {
      return 'Blocks';
    }
    throw new Error(NOT_VALID_SLOT_ERROR);
  }

  public getSlotType(): 'Blocks' | 'OOI' | 'Builder' {
    return this.type;
  }

  public toOOI(): OOISlotPointer {
    if (this.type === 'OOI' && this.slotRef) {
      return { slotRef: this.slotRef, slotId: this.slotId };
    }
    throw new Error('Not an OOI pointer');
  }

  public toBlocks(): BlocksSlotPointer {
    if (this.type === 'Blocks' && this.slotRef) {
      return { slotRef: this.slotRef, slotId: this.slotId };
    }
    throw new Error('Not a blocks pointer');
  }

  public toBuilder(): BuilderSlotPointer {
    if (
      this.type === 'Builder' &&
      this.slotId &&
      this.slotKeyName &&
      this.hostRef
    ) {
      return {
        hostRef: this.hostRef,
        slotId: this.slotId,
        slotKeyName: this.slotKeyName,
        innerElementPath: this.innerElementPath,
      };
    }
    throw new Error('Not a builder pointer');
  }

  public getSlotBiId(): string {
    if (this.slotId) {
      return this.slotId;
    }

    if (this.slotRef) {
      return this.slotRef?.id;
    }
    throw new Error(NOT_VALID_SLOT_ERROR);
  }
  // some external apis (such as platform events) still require slotRef representation
  // TODO after full migration we need to deprecate this method
  public getPlatformEventSlotRef(): CompRef {
    const slotType = this.getSlotType();
    switch (slotType) {
      case 'Blocks':
        return this.toBlocks().slotRef;
      case 'OOI':
        return this.toOOI().slotRef;
      case 'Builder':
        const { slotKeyName, hostRef } = this.toBuilder();
        return {
          id: `${hostRef.id}_builder_${slotKeyName}`,
          type: hostRef.type,
        };
    }
  }

  static parseUniversalSlotPointer(
    documentServicesAPI: DocumentServicesObject,
    manifestAPI: IPublicManifestAPI,
    experimentsAPI: IExperimentsApiAdapter,
    slot: UniversalSlotPointer,
  ): SlotPointerInputParams {
    const isBuilderHost = isBuilderCompRef(
      slot.hostCompRef,
      documentServicesAPI,
    );
    if (isBuilderHost) {
      const all = manifestAPI.get.getAll();
      const type = documentServicesAPI.components.getType(slot.hostCompRef);
      const manifest: EditorElement = all[type]?.manifest?.editorElement;
      const finder = new ManifestSlotPathFinder(manifest);
      const slotPath = finder.findSlotInManifest(slot.slotId);
      return {
        hostRef: slot.hostCompRef,
        slotId: slot.slotId,
        ...slotPath,
      };
    } else {
      const definitions = getSlotDefinitions(
        documentServicesAPI,
        slot.hostCompRef,
        experimentsAPI,
      );
      const definition = definitions.find((d) => d.id === slot.slotId);
      if (!definition) {
        throw new Error('Slot definition not found');
      }
      const refId = definition.blocksSlotRefId
        ? `${slot.hostCompRef.id}${SLOT_ID_DIVIDER}${definition.blocksSlotRefId}`
        : `${slot.hostCompRef.id}${VIRTUAL_OOI_SLOT_SEPARATOR}${definition.name}`;

      return {
        slotRef: {
          id: refId,
          type: slot.hostCompRef.type,
        },
      };
    }
  }
}

function isBuilderCompRef(
  compRef: CompRef,
  documentServicesAPI: DocumentServicesObject,
): boolean {
  const type = documentServicesAPI.components.getType(compRef);
  const builderData =
    documentServicesAPI.builder.components.getCompAppData(type);
  return !!builderData;
}
